import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Button, FormError, LinkButton } from "@offstreetparking/design-system/forms";
import { AddIcon } from "@offstreetparking/design-system/icons/AddIcon";
import { zodPlateValidation } from "@offstreetparking/domain/entities/vehicle";
import type { CountryValue } from "@offstreetparking/geography";
import { useFieldArray, useWatch } from "react-hook-form";
import * as z from "zod/v4";
import type { RouterOutputs } from "../../../../api/types";
import { VehicleInput } from "../../../CreatePermitWizard/components/ParkingDetailsForm/VehicleInput";
import { vehicleKey } from "../../../CreatePermitWizard/components/ParkingDetailsForm/parker-vehicle";
import { Form } from "../../../forms/Form";
import { LoggedInVehicleSelector } from "./LoggedInVehicleSelector";
import { VehicleChangesRemainingNote } from "./VehicleChangesRemainingNote";
import { planPermitVehicleChanges } from "./plan-permit-vehicle-changes";

export type EditPermitVehiclesFormVehicle = {
	id?: number;
	plate: string;
	state: string;
};
type ParkerVehicle = RouterOutputs["parkers"]["getParkerVehicles"]["items"][number];
type EditPermitVehiclesFormProps = {
	vehicles: Pick<ParkerVehicle, "id" | "plate" | "state">[];
	country?: string;
	companyState?: string;
	maxVehicleCount?: number;
	changesRemaining: number | null;
	isSubmitting: boolean;
	isParker: boolean;
	onSubmit: (vehicles: EditPermitVehiclesFormVehicle[]) => Promise<void>;
	onCreateVehicle?: () => void;
	onEditVehicle?: (vehicle: ParkerVehicle) => void;
};
export const EditPermitVehiclesForm = ({
	vehicles,
	country,
	companyState,
	maxVehicleCount,
	changesRemaining,
	isSubmitting,
	isParker,
	onSubmit,
	onCreateVehicle,
	onEditVehicle,
}: EditPermitVehiclesFormProps) => {
	const { t } = useLingui();
	const permitVehicleKeys = new Set(vehicles.map(vehicleKey));
	const matchPermitVehicles = (selected: EditPermitVehiclesFormVehicle[]): EditPermitVehiclesFormVehicle[] => {
		if (!isParker) {
			return selected;
		}
		return selected.map((submitted) => {
			const existing = vehicles.find((v) => v.plate === submitted.plate && v.state === submitted.state);
			return {
				id: existing?.id,
				plate: submitted.plate,
				state: submitted.state,
			};
		});
	};
	return (
		<Form
			schema={z
				.object({
					vehicles: z
						.array(
							z.object({
								id: z.number().optional(),
								plate: zodPlateValidation,
								state: z.string().min(1),
							}),
						)
						.min(1)
						.max(maxVehicleCount ?? 1),
				})
				.superRefine((value, ctx) => {
					if (changesRemaining === null) {
						return;
					}
					const plan = planPermitVehicleChanges({
						current: vehicles,
						submitted: matchPermitVehicles(value.vehicles.filter((vehicle) => vehicle.plate)),
					});
					const pendingChanges = plan.vehiclesToUpdate.length + plan.vehiclesToAdd.length;
					if (pendingChanges > changesRemaining) {
						ctx.addIssue({
							code: "custom",
							path: ["vehicles"],
							message: t({
								message: plural(changesRemaining, {
									0: "You have no vehicle changes left on this permit.",
									one: "Only 1 vehicle change is left on this permit. Undo some edits to save.",
									other: "Only # vehicle changes are left on this permit. Undo some edits to save.",
								}),
								comment:
									"Validation error when a parker tries to save more vehicle changes than the permit has left: says there are none left, or how many are left and asks them to undo some edits",
							}),
						});
					}
				})}
			initialValues={{
				vehicles: vehicles.map((vehicle) => ({
					id: vehicle.id,
					plate: vehicle.plate,
					state: vehicle.state,
				})),
			}}
			onSubmit={async ({ vehicles: submittedVehicles }) => {
				await onSubmit(matchPermitVehicles(submittedVehicles));
			}}
			className="space-y-4"
		>
			{({ control, register, formState: { errors } }) => {
				const {
					fields,
					append: addVehicle,
					remove: removeVehicle,
				} = useFieldArray({
					control,
					name: "vehicles",
				});
				const watchedVehicles = useWatch({
					control,
					name: "vehicles",
				});
				const plan = planPermitVehicleChanges({
					current: vehicles,
					submitted: matchPermitVehicles(watchedVehicles.filter((vehicle) => vehicle.plate)),
				});
				const pendingChanges = plan.vehiclesToUpdate.length + plan.vehiclesToAdd.length;
				const isSingleSelect = (maxVehicleCount ?? 1) === 1;
				const budgetExhausted =
					changesRemaining !== null &&
					(isSingleSelect ? changesRemaining === 0 : changesRemaining - pendingChanges <= 0);
				const selectableVehicleKeys = budgetExhausted ? permitVehicleKeys : undefined;
				const lockedTapMessage =
					changesRemaining === 0
						? t({
								message: "You have no vehicle changes left.",
								comment:
									"Heading above the vehicle list in the edit permit vehicles dialog, shown in red after a parker taps a locked vehicle when the permit has no self-service vehicle changes left",
							})
						: t({
								message: "No changes left. Select Cancel to undo yours.",
								comment:
									"Heading above the vehicle list in the edit permit vehicles dialog, shown in red after a parker taps a locked vehicle when their unsaved edits have used up the permit's remaining self-service vehicle changes",
							});
				const hasVehicleChangesLeft = changesRemaining === null || changesRemaining - pendingChanges > 0;
				const canAddVehicle = !isParker && fields.length < (maxVehicleCount ?? 1) && hasVehicleChangesLeft;
				const vehiclesError = errors.vehicles?.root?.message ?? errors.vehicles?.message;
				return (
					<>
						{isParker && (
							<div className="flex min-h-[60vh] flex-col gap-6 sm:min-h-0">
								<div className="flex-1">
									<LoggedInVehicleSelector
										isLoggedIn
										maxVehicles={maxVehicleCount ?? 1}
										initialVehicles={vehicles}
										selectableVehicleKeys={selectableVehicleKeys}
										lockedTapMessage={lockedTapMessage}
										onCreateVehicle={() => onCreateVehicle?.()}
										onEditVehicle={(vehicle) => onEditVehicle?.(vehicle)}
									/>
								</div>
								<div className="-mx-4 -mb-6 sticky bottom-0 space-y-3 border-neutral-faded border-t bg-white px-4 py-4 sm:relative sm:m-0 sm:border-0 sm:p-0">
									{changesRemaining !== null && (
										<div aria-live="polite" aria-atomic="true">
											<VehicleChangesRemainingNote
												changesRemaining={changesRemaining}
												pendingChanges={pendingChanges}
												pendingRemovals={plan.vehiclesToRemove.length}
											/>
										</div>
									)}
									<Button type="submit" isLoading={isSubmitting} className="w-full">
										{t`Save`}
									</Button>
								</div>
							</div>
						)}
						{!isParker && (
							<>
								<div className="space-y-2">
									{fields.map((field, index) => (
										<VehicleInput
											key={field.id}
											index={index}
											register={register}
											errors={errors}
											country={country as CountryValue}
											showRemove={fields.length > 1}
											isDisabled={changesRemaining === 0}
											onRemove={() => removeVehicle(index)}
										/>
									))}
								</div>
								{canAddVehicle && (
									<LinkButton
										type="button"
										size="sm"
										onClick={() =>
											addVehicle({
												plate: "",
												state: companyState ?? "",
											})
										}
										disabled={isSubmitting}
									>
										<AddIcon className="mr-1 h-4 w-4" />
										{t`Add Vehicle`}
									</LinkButton>
								)}
								{changesRemaining !== null && vehiclesError && <FormError error={vehiclesError} />}
								{changesRemaining !== null && (
									<div aria-live="polite" aria-atomic="true">
										<VehicleChangesRemainingNote
											changesRemaining={changesRemaining}
											pendingChanges={pendingChanges}
											pendingRemovals={plan.vehiclesToRemove.length}
										/>
									</div>
								)}
								<div className="flex justify-end">
									<Button type="submit" isLoading={isSubmitting} className="w-full sm:w-auto">
										{t`Save`}
									</Button>
								</div>
							</>
						)}
					</>
				);
			}}
		</Form>
	);
};
